import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Search, 
  Filter, 
  Download, 
  MoreHorizontal, 
  Package,
  ArrowUp,
  ArrowDown,
  Calendar
} from "lucide-react";
import { useState, useEffect } from "react";
import type { ProcessedInventoryItem } from "@/hooks/useFactoryData";

interface DataTableProps {
  data?: ProcessedInventoryItem[];
}

// 默认数据
const defaultData: ProcessedInventoryItem[] = [];

export function DataTable({ data = defaultData }: DataTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState(data);
  const [columns, setColumns] = useState<string[]>([]);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
  const [availableValues, setAvailableValues] = useState<Record<string, string[]>>({});

  // 应用搜索和筛选
  const applyFilters = () => {
    let filtered = data;
    
    // 应用搜索
    if (searchTerm) {
      const searchValue = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        return columns.some(column => {
          const cellValue = item[column as keyof ProcessedInventoryItem];
          return cellValue && String(cellValue).toLowerCase().includes(searchValue);
        });
      });
    }
    
    // 应用列筛选
    Object.keys(columnFilters).forEach(column => {
      const selectedValues = columnFilters[column];
      if (selectedValues.length > 0) {
        filtered = filtered.filter(item => {
          const cellValue = String(item[column as keyof ProcessedInventoryItem] || '');
          return selectedValues.includes(cellValue);
        });
      }
    });
    
    setFilteredData(filtered);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };
  
  // 生成每列的可选值
  const generateAvailableValues = () => {
    if (data.length === 0) return {};
    
    const values: Record<string, Set<string>> = {};
    
    columns.forEach(column => {
      values[column] = new Set();
      data.forEach(item => {
        const value = String(item[column as keyof ProcessedInventoryItem] || '');
        if (value && value !== '-') {
          values[column].add(value);
        }
      });
    });
    
    // 转换为数组并排序
    const sortedValues: Record<string, string[]> = {};
    Object.keys(values).forEach(column => {
      sortedValues[column] = Array.from(values[column]).sort();
    });
    
    setAvailableValues(sortedValues);
  };

  // 处理列筛选
  const handleColumnFilter = (column: string, value: string, checked: boolean) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      if (!newFilters[column]) {
        newFilters[column] = [];
      }
      
      if (checked) {
        if (!newFilters[column].includes(value)) {
          newFilters[column] = [...newFilters[column], value];
        }
      } else {
        newFilters[column] = newFilters[column].filter(v => v !== value);
        if (newFilters[column].length === 0) {
          delete newFilters[column];
        }
      }
      
      return newFilters;
    });
  };

  // 清除某列的筛选
  const clearColumnFilter = (column: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[column];
      return newFilters;
    });
  };

  // 清除所有筛选
  const clearAllFilters = () => {
    setColumnFilters({});
    setSearchTerm('');
  };

  // 当数据、搜索词或筛选条件变化时更新过滤数据
  useEffect(() => {
    applyFilters();
  }, [data, searchTerm, columnFilters]);

  // 当数据变化时生成列信息和可选值
  useEffect(() => {
    if (data.length > 0) {
      const firstItem = data[0];
      const allKeys = Object.keys(firstItem);
      const dynamicColumns = allKeys.filter(key => 
        key !== 'id' && key !== 'quantity' && key !== 'status' && 
        key !== 'inboundDate' && key !== 'expiryDate'
      );
      setColumns(dynamicColumns);
    }
  }, [data]);

  // 当列信息变化时生成可选值
  useEffect(() => {
    generateAvailableValues();
  }, [columns, data]);

  // 渲染单元格内容
  const renderCellValue = (row: ProcessedInventoryItem, column: string) => {
    const value = row[column as keyof ProcessedInventoryItem];
    
    // 特殊字段的渲染逻辑
    if (column === 'tran_type') {
      return (
        <div className="flex items-center space-x-2">
          {getTypeIcon(String(value))}
          <span className="text-sm">{String(value)}</span>
        </div>
      );
    }
    
    if (column === 'factory_stock_status') {
      return (
        <Badge variant={getStatusVariant(String(value))}>
          {String(value)}
        </Badge>
      );
    }
    
    if (column === 'material_category') {
      return (
        <Badge 
          variant="outline" 
          className={getMaterialTypeColor(String(value))}
        >
          {String(value)}
        </Badge>
      );
    }
    
    if (column === '入库数量') {
      return (
        <div className="text-right font-mono">
          <span className="font-medium">
            {Number(value).toLocaleString()}
          </span>
          <span className="text-muted-foreground ml-1">
            件
          </span>
        </div>
      );
    }
    
    if (column === '工厂执行日期' || column === '同步到maple时间') {
      return (
        <div className="flex items-center space-x-1">
          <Calendar className="h-3 w-3" />
          <span className="text-xs">{String(value)}</span>
        </div>
      );
    }
    
    // 默认渲染
    return <span className="text-sm">{String(value || '-')}</span>;
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case '放行':
        return 'default';
      case '待检':
        return 'outline';
      case '完备':
        return 'default';
      case '充足':
        return 'secondary';
      case '进行中':
        return 'outline';
      case '待处理':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getTypeIcon = (type: string) => {
    if (!type) return <Package className="h-4 w-4 text-blue-600" />;
    
    if (type.includes('入库')) {
      return <ArrowUp className="h-4 w-4 text-green-600" />;
    } else if (type.includes('出库')) {
      return <ArrowDown className="h-4 w-4 text-red-600" />;
    }
    return <Package className="h-4 w-4 text-blue-600" />;
  };

  const getMaterialTypeColor = (materialType: string) => {
    switch (materialType) {
      case '001': return 'bg-blue-100 text-blue-800';
      case '002': return 'bg-green-100 text-green-800';
      case '003': return 'bg-purple-100 text-purple-800';
      case '原料': return 'bg-blue-100 text-blue-800';
      case '商品': return 'bg-green-100 text-green-800';
      case '包材': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card className="w-full bg-cream-80">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-16">数据记录</CardTitle>
          <div className="flex items-center space-x-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="搜索记录..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-8 w-64 text-14"
              />
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="text-12">
                  <Filter className="h-4 w-4 mr-2" />
                  筛选
                  {Object.keys(columnFilters).length > 0 && (
                    <Badge variant="destructive" className="ml-2 px-1 py-0 text-xs">
                      {Object.keys(columnFilters).length}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80" align="end">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">列筛选</h4>
                    {Object.keys(columnFilters).length > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={clearAllFilters}
                        className="text-xs"
                      >
                        清除全部
                      </Button>
                    )}
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto space-y-3">
                    {columns.map(column => {
                      const values = availableValues[column] || [];
                      const selectedValues = columnFilters[column] || [];
                      
                      if (values.length === 0) return null;
                      
                      return (
                        <div key={column} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">{column}</Label>
                            {selectedValues.length > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => clearColumnFilter(column)}
                                className="text-xs h-6 px-2"
                              >
                                清除
                              </Button>
                            )}
                          </div>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {values.map(value => (
                              <div key={value} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`${column}-${value}`}
                                  checked={selectedValues.includes(value)}
                                  onCheckedChange={(checked) => 
                                    handleColumnFilter(column, value, checked as boolean)
                                  }
                                />
                                <Label 
                                  htmlFor={`${column}-${value}`}
                                  className="text-sm cursor-pointer flex-1 truncate"
                                  title={value}
                                >
                                  {value}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {columns.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      暂无可筛选的列
                    </p>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="outline" size="sm" className="text-12">
              <Download className="h-4 w-4 mr-2" />
              导出
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead key={column} className="text-center">
                    {column}
                  </TableHead>
                ))}
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.map((row) => (
                <TableRow key={row.id} className="hover:bg-muted/50">
                  {columns.map((column) => (
                    <TableCell key={column} className="text-center">
                      {renderCellValue(row, column)}
                    </TableCell>
                  ))}
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>操作</DropdownMenuLabel>
                        <DropdownMenuItem>查看详情</DropdownMenuItem>
                        <DropdownMenuItem>追踪物流</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem>导出记录</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        
        {filteredData.length === 0 && data.length === 0 && (
          <div className="text-center py-8">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">暂无数据记录</p>
          </div>
        )}
        
        {filteredData.length === 0 && data.length > 0 && (
          <div className="text-center py-8">
            <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">没有找到匹配的记录</p>
          </div>
        )}
        
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-4">
            <p className="text-sm text-muted-foreground">
              显示 {filteredData.length} 条记录，共 {data.length} 条
              {(searchTerm || Object.keys(columnFilters).length > 0) && (
                <span className="text-blue-600">（已筛选）</span>
              )}
            </p>
            {(searchTerm || Object.keys(columnFilters).length > 0) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                清除所有筛选
              </Button>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" disabled>
              上一页
            </Button>
            <Button variant="outline" size="sm" disabled>
              下一页
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}